import {Carousel} from '@mantine/carousel';
import {Image, Skeleton} from '@mantine/core';
import Autoplay from 'embla-carousel-autoplay';
import type React from 'react';
import {useRef, useState} from 'react';
import {MdPlayCircleFilled} from 'react-icons/md';
import type {GeneralCarouselItem} from '../../../../core/entities/generalCarousel/schema';
import {cn} from '../../../../lib/classname';
import {CommonLink} from '../../../../shared/components/CommonLink';
import {
  getYouTubeEmbedURL,
  getYouTubeThumbnailURL,
} from '../../usecases/youtubeEmbedURL';

const buildAutoplayEmbedSrc = (embedURL: string): string => {
  const url = new URL(embedURL);
  url.searchParams.set('autoplay', '1');
  url.searchParams.set('mute', '1');
  url.searchParams.set('controls', '0');
  url.searchParams.set('playsinline', '1');
  url.searchParams.set('iv_load_policy', '3');
  return url.toString();
};

const YouTubeEmbed: React.FC<{
  embedURL: string;
  title: string;
  thumbnailURL?: string;
  isPlaying: boolean;
  onPlayingChange: (isPlaying: boolean) => void;
}> = ({embedURL, title, thumbnailURL, isPlaying, onPlayingChange}) => (
  <div
    className="rounded-16 overflow-hidden"
    onMouseEnter={() => onPlayingChange(true)}
    onMouseLeave={() => onPlayingChange(false)}
  >
    {isPlaying ? (
      <iframe
        src={buildAutoplayEmbedSrc(embedURL)}
        title={title}
        className="block w-full aspect-[2/1] border-0"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox"
      />
    ) : (
      <button
        type="button"
        aria-label={`${title}を再生`}
        className="relative block w-full cursor-pointer border-0 p-0 bg-transparent"
        onClick={() => onPlayingChange(true)}
      >
        <Image
          src={thumbnailURL}
          alt={title}
          fit="cover"
          imageProps={{loading: 'lazy'}}
          classNames={{image: 'block w-full aspect-[2/1]'}}
        />
        <span className="absolute inset-0 flex items-center justify-center">
          <MdPlayCircleFilled size={64} className="text-white drop-shadow" />
        </span>
      </button>
    )}
  </div>
);

type Props = {
  componentId: string;
  items: GeneralCarouselItem[];
  isLoading: boolean;
  autoScroll?: boolean;
  itemScrollInterval?: number;
  infiniteLoop?: boolean;
  isPC: boolean;
};

export const GeneralCarouselPresentation: React.FC<Props> = ({
  componentId,
  items,
  isLoading,
  autoScroll = false,
  itemScrollInterval = 4000,
  infiniteLoop = false,
  isPC,
}) => {
  const autoplay = useRef(
    Autoplay({
      delay: itemScrollInterval,
      stopOnInteraction: false,
    }),
  );
  const [playingItemID, setPlayingItemID] = useState<number | null>(null);

  const onMouseEnter = autoplay.current.stop;
  const onMouseLeave = autoplay.current.reset;
  if (isLoading) {
    return (
      <div
        className="flex overflow-x-scroll mt-16 gap-16"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {Array.from({length: 3}).map((_, index) => (
          <div
            key={`skeleton-${index}`}
            className="flex-shrink-0 w-[370px] max-w-[90vw]"
          >
            <Skeleton height={200} className="rounded-16" />
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  // Carousel使わない
  if (isPC) {
    return (
      <div
        id={componentId}
        className={cn(
          'flex w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] overflow-x-auto',
          {
            'justify-center': items.length <= 3,
          },
        )}
      >
        <div className="mx-auto flex gap-16 px-4 max-w-[1200px]">
          {items.map((item, index) => {
            const youTubeEmbedURL = getYouTubeEmbedURL(item.linkURL);
            return (
              <div
                key={`${item.id}-${index}`}
                className="flex-shrink-0 w-[370px] max-w-[90vw]"
              >
                {youTubeEmbedURL ? (
                  <YouTubeEmbed
                    embedURL={youTubeEmbedURL}
                    title={item.content}
                    thumbnailURL={
                      item.imageURL || getYouTubeThumbnailURL(item.linkURL)
                    }
                    isPlaying={playingItemID === item.id}
                    onPlayingChange={(isPlaying) =>
                      setPlayingItemID(isPlaying ? item.id : null)
                    }
                  />
                ) : (
                  <CommonLink
                    href={item.linkURL}
                    data-gtm-click={`${componentId}-item-${item.displayOrder}`}
                  >
                    <div className="rounded-16 overflow-hidden">
                      <Image
                        src={item.imageURL}
                        alt={item.content}
                        className="w-full h-auto object-cover"
                      />
                    </div>
                  </CommonLink>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div
      id={componentId}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="mt-16"
    >
      <Carousel
        height={200}
        slideSize={370}
        slideGap={16}
        align={'start'}
        plugins={autoScroll ? [autoplay.current] : []}
        loop={autoScroll && infiniteLoop}
        withControls={false}
      >
        {items.map((item, index) => {
          const youTubeEmbedURL = getYouTubeEmbedURL(item.linkURL);
          return (
            <Carousel.Slide
              key={`${item.id}-${index}`}
              className="flex-shrink-0 w-[370px] max-w-[90vw]"
            >
              {youTubeEmbedURL ? (
                <YouTubeEmbed
                  embedURL={youTubeEmbedURL}
                  title={item.content}
                  thumbnailURL={
                    item.imageURL || getYouTubeThumbnailURL(item.linkURL)
                  }
                  isPlaying={playingItemID === item.id}
                  onPlayingChange={(isPlaying) =>
                    setPlayingItemID(isPlaying ? item.id : null)
                  }
                />
              ) : (
                <CommonLink
                  href={item.linkURL}
                  data-general-carousel-id={item.id}
                  data-general-carousel-index={index}
                  data-general-carousel-display-order={item.displayOrder}
                >
                  <div className="rounded-16 overflow-hidden">
                    <Image
                      src={item.imageURL}
                      alt={item.content}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </CommonLink>
              )}
            </Carousel.Slide>
          );
        })}
      </Carousel>
    </div>
  );
};
